import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    deleteParticipantJobSearch,
    loadParticipantJobSearch
} from '../../../store/participantService';
import {
    DEFAULT_TABLE_ROWS_PER_PAGE,
    EDIT_ON_CLOSED_ROLES
} from '../../../utils/formValidation/commonConstants';
import { PARTICIPANT_JOB_SEARCH_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateEditJobSearch from './CreateEditJobSearch';
import SupportedJobSearchTableRow from './SupportedJobSearchTableRow';

const headCells = [
    { id: 'dateApplied', numeric: false, label: 'Date Applied' },
    { id: 'jobSectorId', numeric: false, label: 'Job Sector' },
    { id: 'businessName', numeric: false, label: 'Business Name' },
    { id: 'jobTitle', numeric: false, label: 'Job Title' },
    { id: 'interviewDate', numeric: false, label: 'Interview Date' },
    { id: 'interviewAttended', numeric: false, label: 'Interview Attended' },
    { id: 'jobOffered', numeric: false, label: 'Job Offered' },
    { id: 'jobAccepted', numeric: false, label: 'Job Accepted' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'dateApplied',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const deleteRoles = [QUALITY, SUPERUSER];

const SupportedJobSearchManagement = ({ isViewOnly = false }) => {
    // HOOKS
    const dispatch = useDispatch();
    // LOCAL STATE
    const [editJobSearchOpen, setEditJobSearchOpen] = useState(false);
    const [formType, setFormType] = useState('create');
    const [jobSearchToEdit, setJobSearchToEdit] = useState({});
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantJobSearch = useSelector(
        (state) => state.entities.participantService.currentParticipantJobSearch
    );
    const loadingParticipantJobSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantJobSearch
    );

    // USE EFFECTS
    useEffect(() => {
        if (!currentParticipant?.id) return;
        dispatch(loadParticipantJobSearch(currentParticipant.id));

        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus || isViewOnly);
        setIsDeleteDisabled(!hasRole(deleteRoles, roles) || isDisabledOnClosedStatus || isViewOnly);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (!currentParticipantJobSearch?.length && !loadingParticipantJobSearch)
            setLoadingContentMessage(PARTICIPANT_JOB_SEARCH_ERROR);
    }, [currentParticipantJobSearch]);

    // HELPER FN
    const createRows = () => {
        return (
            stableSort(currentParticipantJobSearch, rowMetaData.orderBy, rowMetaData.order).map(
                (el) => {
                    return (
                        <SupportedJobSearchTableRow
                            key={el.id}
                            jobSearchEntry={el}
                            onEdit={onEditJobSearch}
                            onDelete={onDeleteJobSearch}
                            isDisabled={isDisabled}
                            isDeleteDisabled={isDeleteDisabled}
                        />
                    );
                }
            ) || []
        );
    };

    // EVENT HANDLERS
    const onDeleteJobSearch = (id) => {
        dispatch(deleteParticipantJobSearch(id));
    };

    const onEditJobSearch = (jobSearch) => {
        setJobSearchToEdit(jobSearch);
        setFormType('edit');
        setEditJobSearchOpen(true);
    };

    const onCreateJobSearch = () => {
        setFormType('create');
        setEditJobSearchOpen(true);
    };

    const onCloseEditJobSearch = () => {
        setJobSearchToEdit({});
        setEditJobSearchOpen(false);
    };

    // RENDER
    return (
        <div data-testid="supported_job_search">
            {!currentParticipantJobSearch?.length ? (
                <LoadingSpinner content={loadingContentMessage} />
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={currentParticipantJobSearch.length}
                />
            )}
            {editJobSearchOpen && currentParticipant?.contractId ? (
                <CreateEditJobSearch
                    row={jobSearchToEdit}
                    formType={formType}
                    onCancel={onCloseEditJobSearch}
                    isDisabled={isDisabled}
                />
            ) : (
                <Button
                    id="AddJobSearchButton"
                    disabled={isDisabled}
                    content="Add Job Search"
                    onClick={onCreateJobSearch}
                />
            )}
        </div>
    );
};

SupportedJobSearchManagement.propTypes = {
    isViewOnly: PropTypes.bool
};

export default SupportedJobSearchManagement;
